import { type FC } from "react";
import { useTranslation } from "react-i18next";
import DashboardKpiCard from "src/components/common/DashboardKpiCard";
import type { DefinitionTooltipInfo } from "src/components/common/DefinitionTooltip";

type RestaurantKpiAccent = "primary" | "success" | "warning" | "destructive";

interface RestaurantKpiCardProps {
  label: string;
  value: string;
  delta?: number;
  accent?: RestaurantKpiAccent;
  comparisonLabel?: string;
  tooltip?: DefinitionTooltipInfo | string;
  tooltipAlign?: "start" | "end";
}

const ACCENT_CLASS: Record<RestaurantKpiAccent, string> = {
  primary: "restaurant-kpi-card--primary",
  success: "restaurant-kpi-card--success",
  warning: "restaurant-kpi-card--warning",
  destructive: "restaurant-kpi-card--destructive",
};

const RestaurantKpiCard: FC<RestaurantKpiCardProps> = ({
  label,
  value,
  delta,
  accent = "primary",
  comparisonLabel,
  tooltip,
  tooltipAlign = "start",
}) => {
  const { t } = useTranslation();

  return (
  <DashboardKpiCard
    className={`restaurant-kpi-card ${ACCENT_CLASS[accent]}`}
    title={label}
    value={value}
    changePercent={delta}
    comparisonLabel={
      comparisonLabel ??
      t("restaurantAnalytics.kpi.vsLastPeriod", { defaultValue: "vs previous period" })
    }
    hideNoTrendFallback={delta === undefined}
    tone={accent === "destructive" ? "negative" : accent === "warning" ? "neutral" : "positive"}
    trendPolarity={accent === "destructive" ? "lower-is-better" : "higher-is-better"}
    disableChangeFallback
    uppercaseTitle={false}
    tooltip={typeof tooltip === "string" ? { description: tooltip } : tooltip}
    tooltipAlign={tooltipAlign}
  />
  );
};

export default RestaurantKpiCard;
